import api from './client';
import { readGuestToken, writeGuestToken, clearGuestToken } from './guestToken.js';

const GUEST_TOKEN_KEY = 'guestToken';
// The previous identity, kept when one is genuinely replaced. A guest's work is
// owned by their token's `sub`; discarding the token makes that work
// unreachable by anyone, including the person who made it.
const GUEST_TOKEN_RECOVERY_KEY = 'guestToken_previous';
// The heartbeat a guest page writes while it is open (see markInUse).
const GUEST_IN_USE_KEY = 'guest_session_in_use';
// Which Briefing this device last worked on (written by the home page and the
// guest conversation).
const GUEST_ACTIVE_PROJECT_KEY = 'guest_active_project_id';
const GUEST_IN_USE_TTL_MS = 30_000;
const GUEST_IN_USE_BEAT_MS = 10_000;

// AI-heavy guest endpoints (interact, finalize) can take 10–30s per call
// because Newton runs an LLM round-trip on every answer. The default 10s
// axios timeout fired too early and surfaced as REQS.INTERACTION_FAILED
// even though the server completed cleanly. Pin these to 60s.
const AI_TIMEOUT_MS = 60000;

// RF-043: generated once, synchronously, at module load — not inside the
// async mint call — so every caller in this page's lifetime (including two
// effects that both see "no token yet" on the same render, or StrictMode's
// double-invoke) sends the SAME key. The server collapses concurrent mints
// that share it into one session instead of silently creating two guests,
// one of whom owns the project this device just created and the other of
// whom is what `localStorage` ends up holding.
const MINT_KEY = (() => {
  try { return crypto.randomUUID(); } catch { return `${Date.now()}-${Math.random()}`; }
})();

class GuestAPI {
  static getToken() {
    return readGuestToken();
  }

  static setToken(token) {
    if (!token) return;
    writeGuestToken(token);
  }

  static clearToken() {
    clearGuestToken();
  }

  /**
   * 🧹 "Not yours? Clear it" (GC-002).
   *
   * A guest's Briefings belong to whoever holds this device's guest token, and
   * the home page offers them to whoever opens the browser next — a shared or
   * library computer handed one visitor's idea to the next. Clearing forgets
   * EVERY guest identity this device holds: the current token, the recovery
   * ring (or "Recover my previous session" would put the same identity back),
   * the remembered Briefing pointer and the in-use beat. The work stays on the
   * server under its own token; this device simply stops being able to open it.
   */
  static forgetDevice() {
    for (const key of [GUEST_TOKEN_KEY, GUEST_TOKEN_RECOVERY_KEY, GUEST_ACTIVE_PROJECT_KEY, GUEST_IN_USE_KEY]) {
      try { localStorage.removeItem(key); } catch (_) { /* private mode */ }
    }
  }

  /**
   * Is the token we hold still within its own lifetime?
   *
   * Read locally, from the token's own `exp`. This is not a security check —
   * the server decides that — it is the difference between "our credential
   * ran out" and "the server said no for some other reason", and those two
   * need opposite responses.
   */
  static tokenIsLive(token = this.getToken()) {
    if (!token) return false;
    try {
      const [, payload] = String(token).split('.');
      const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      if (!claims?.exp) return true; // no expiry claimed — not ours to expire
      // A few seconds of slack for clock skew, so a token expiring this
      // instant is treated as expired rather than live-then-rejected.
      return claims.exp * 1000 > Date.now() + 5000;
    } catch (_) {
      return false; // unreadable is not live
    }
  }

  /**
   * What to do about a 401 — and specifically, when NOT to become somebody else.
   *
   * ⚠️ Re-minting a guest session is DESTRUCTIVE. A guest's work is owned by the
   * `sub` in their token, so a new session is a new person who owns none of it.
   * Both retry helpers used to answer every 401 by clearing the token and
   * minting a new one, which meant a single transient 401 silently orphaned
   * everything the visitor had done.
   *
   * Observed, not theorised: a gateway restart mid-session re-minted the token,
   * and the guest's own finished interview answered 404 on the report they had
   * just been told was building. The screen said "Request failed with status
   * code 404" and there was no way back — no link, no notice, no mention that
   * anything had been lost. In production that is every active guest, on every
   * deploy.
   *
   * So: a token that is still within its lifetime is retried AS ITSELF. A 401
   * on a live token is the server having a moment — a restart, a key rotation,
   * a race — and the answer to that is to ask again, not to throw away the
   * visitor's identity. Only a genuinely expired or unreadable token is
   * replaced, and the old one is kept under a recovery key so the work it owns
   * can still be found rather than being unreachable forever.
   *
   * @returns {Promise<'retry-same'|'reminted'>}
   */
  static async recoverFrom401() {
    const current = this.getToken();
    if (this.tokenIsLive(current)) return 'retry-same';
    if (current) {
      // Kept, not discarded: this token is the only thing that names the owner
      // of whatever the visitor built. Recovery needs it to exist.
      this.rememberPrevious(current);
    }
    this.clearToken();
    await this.ensureSession();
    return 'reminted';
  }

  // The one mint in flight, so two callers that both see "no token" share a
  // session instead of each becoming a different visitor.
  //
  // Observed, not theorised: the home page minted TWO guests on load (the
  // project list and the landing effect both asked at once), the project was
  // created under whichever `setToken` landed last, and the interview page then
  // ran as the other one — 403 on the first tap, on a project the visitor had
  // just created. Four `guest_<uuid>` identities in one journey.
  static #minting = null;

  static async ensureSession() {
    const existing = this.getToken();
    if (existing) return existing;
    if (this.#minting) return this.#minting;

    this.#minting = (async () => {
      try {
        // Re-read after the await gap: a sibling may have stored a token while
        // this promise was being created.
        const again = this.getToken();
        if (again) return again;
        const response = await api.post('/guest/session', {}, { headers: { 'x-guest-mint-key': MINT_KEY } });
        const token = response.data?.data?.guestToken;
        this.setToken(token);
        return token;
      } finally {
        this.#minting = null;
      }
    })();
    return this.#minting;
  }

  /**
   * "Somebody is in this session right now" — a heartbeat other tabs can read.
   *
   * localStorage is shared by every tab on the origin. A tab signed in as a
   * member runs the background guest-claim on every session hydration, and it
   * reads the SAME `guestToken` a guest tab is answering questions with — so
   * the guest's project was moved into the member's account and the guest
   * tab answered 403 mid-interview. Seen live on 2026-09-04, with the
   * `storage` event naming the other tab.
   *
   * The guest page beats this key every few seconds while it is open; the
   * claim stays away from a session whose beat is fresh. Once the guest leaves,
   * the beat goes stale and the claim may proceed — which is the case it is
   * for: sign in somewhere else, and the work follows you.
   */
  static markInUse(projectId) {
    try {
      localStorage.setItem(GUEST_IN_USE_KEY, JSON.stringify({ projectId: String(projectId || ''), at: Date.now() }));
    } catch (_) { /* private mode */ }
  }

  static clearInUse() {
    try { localStorage.removeItem(GUEST_IN_USE_KEY); } catch (_) { /* private mode */ }
  }

  static inUse(now = Date.now()) {
    try {
      const raw = localStorage.getItem(GUEST_IN_USE_KEY);
      if (!raw) return false;
      const { at } = JSON.parse(raw);
      return Number(at) > 0 && now - Number(at) < GUEST_IN_USE_TTL_MS;
    } catch (_) {
      return false;
    }
  }

  /**
   * Put the previous identity back, when there is one and it is still live.
   *
   * The token under the recovery key names the owner of whatever the visitor
   * built before it was replaced. If it has not expired, becoming that person
   * again is the only way back to their interview. Returns true when it did.
   */
  // 🧵 EVERY identity this device has held, newest first — not just the last.
  //
  // The recovery key held ONE token, so a second re-mint overwrote the first.
  // Walked 2026-09-08: a guest built a whole Briefing as `guest_938a…`, the
  // gateway restarted twice, and by the time the dead end appeared the
  // recovery key held `guest_f0d7…` — an identity that owned nothing. The
  // page offered "Start over" because the one token it kept could not open
  // the work (walk log #43, #62). Five is plenty: past that the tokens have
  // expired anyway.
  static #RECOVERY_DEPTH = 5;

  static previousTokens() {
    let raw = null;
    try { raw = localStorage.getItem(GUEST_TOKEN_RECOVERY_KEY); } catch (_) { return []; }
    if (!raw) return [];
    // A single token was stored here before this was a list; read both shapes.
    let list;
    try { list = JSON.parse(raw); } catch (_) { list = [raw]; }
    if (!Array.isArray(list)) list = [raw];
    return list.filter((t) => typeof t === 'string' && this.tokenIsLive(t));
  }

  static rememberPrevious(token) {
    if (!token) return;
    const next = [token, ...this.previousTokens().filter((t) => t !== token)].slice(0, this.#RECOVERY_DEPTH);
    try { localStorage.setItem(GUEST_TOKEN_RECOVERY_KEY, JSON.stringify(next)); } catch (_) { /* private mode */ }
  }

  // Is there a previous identity worth offering to restore? A "Recover my
  // previous session" button that always fails is a promise the page cannot
  // keep; it is shown only when a live token is actually held.
  static hasPreviousSession() {
    return this.previousTokens().length > 0;
  }

  /**
   * Put a previous identity back.
   *
   * With a `probe` — an async (token) => boolean the caller supplies, normally
   * "can this token open the project I am looking at" — every kept identity is
   * tried until one can, which is the only way to pick the right one when the
   * device has held several. Without a probe the newest live one is taken, as
   * before.
   */
  static async restorePreviousSession({ probe = null } = {}) {
    const kept = this.previousTokens();
    if (!kept.length) return false;
    const current = this.getToken();
    for (const token of kept) {
      // eslint-disable-next-line no-await-in-loop -- each probe is one HTTP
      // call and there are at most five; parallel probes would race setToken.
      if (probe && !(await probe(token).catch(() => false))) continue;
      this.setToken(token);
      // The one that worked leaves the ring; the others stay, because a second
      // dead end later may need one of them.
      const rest = kept.filter((t) => t !== token);
      if (current && current !== token) rest.unshift(current);
      try { localStorage.setItem(GUEST_TOKEN_RECOVERY_KEY, JSON.stringify(rest.slice(0, this.#RECOVERY_DEPTH))); } catch (_) { /* private mode */ }
      return true;
    }
    return false;
  }

  static async createProject(payload) {
    return this._withTokenRetry(async (token) => {
      const response = await api.post('/guest/projects', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.data;
    });
  }

  static async listProjects() {
    return this._withTokenRetry(async (token) => {
      const response = await api.get('/guest/projects', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.data;
    });
  }

  static async startRequirements(projectId) {
    return this._withTokenRetry(async (token) => {
      const response = await api.post('/guest/requirements/start', { projectId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.data;
    });
  }

  static async interactRequirements(projectId, answerText) {
    return this._withTokenRetry(async (token) => {
      const response = await api.post('/guest/requirements/interact', { projectId, answerText }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: AI_TIMEOUT_MS
      });
      return response.data.data;
    });
  }

  static async restartRequirements(projectId) {
    return this._withTokenRetry(async (token) => {
      const response = await api.post(`/guest/requirements/${projectId}/restart`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.data;
    });
  }

  static async finalize(projectId) {
    return this._withTokenRetry(async (token) => {
      const response = await api.post('/guest/orchestrator/finalize', { projectId }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: AI_TIMEOUT_MS
      });
      return response.data.data;
    });
  }

  static async jobStatus(jobId) {
    return this._withTokenRetry(async (token) => {
      const response = await api.get(`/guest/jobs/${jobId}/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.data;
    });
  }

  static async sharePreview(sessionId) {
    return this._withTokenRetry(async (token) => {
      const response = await api.post(`/guest/preview/${sessionId}/share-link`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.data;
    });
  }

  static async generatePreview(projectId) {
    return this._withTokenRetry(async (token) => {
      const response = await api.post(`/guest/preview/${projectId}/generate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.data;
    });
  }

  /**
   * One-shot helper for the "Share for review" CTA on the guest completion
   * screen. Ensures a preview session exists for the project, then mints a
   * shareable URL on it. Returns the absolute URL the guest can hand to a
   * reviewer.
   */
  static async getShareLink(projectId) {
    const generated = await this.generatePreview(projectId);
    const sessionId = generated?.sessionId || generated?._id;
    if (!sessionId) throw new Error('Preview session was not created.');
    const shared = await this.sharePreview(sessionId);
    const relative = shared?.security?.shareUrl || '';
    if (!relative) throw new Error('Share link was not returned.');
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return {
      shareUrl: relative.startsWith('http') ? relative : `${origin}${relative}`,
      sessionId,
      expiresAt: shared?.security?.expiresAt || null,
      slug: shared?.slug || null
    };
  }

  static async _withTokenRetry(requestFn) {
    try {
      const token = await this.ensureSession();
      return await requestFn(token);
    } catch (error) {
      if (error?.response?.status !== 401) throw error;
      // See recoverFrom401: a live token is retried as itself. This used to
      // clear unconditionally, which answered a transient 401 by becoming a
      // different visitor who owned none of the work.
      const outcome = await this.recoverFrom401();
      const token = await this.ensureSession();
      try {
        return await requestFn(token);
      } catch (again) {
        // 🔑 A TOKEN THE SERVER WILL NOT ACCEPT IS DEAD, WHATEVER ITS EXPIRY.
        //
        // `tokenIsLive` reads the `exp` claim, which is the client's only way
        // to judge a token it cannot verify. So a token the SERVER rejects —
        // a rotated signing key, a revoked session — is "live" here forever:
        // retried as itself, refused again, and every later call repeats the
        // pair. The page then shows a fresh greeting on a URL that names an
        // existing Briefing, because nothing ever concluded the identity was
        // gone (walk log, round two).
        //
        // Twice is enough to conclude it. The token joins the recovery ring
        // rather than being discarded — it still names the owner of whatever
        // the visitor built, and "Recover my previous session" probes the ring
        // against the project.
        if (again?.response?.status === 401 && outcome === 'retry-same') {
          const dead = this.getToken();
          if (dead) this.rememberPrevious(dead);
          this.clearToken();
          await this.ensureSession();
        }
        throw again;
      }
    }
  }
}

export { GuestAPI as guest, GUEST_TOKEN_KEY, GUEST_TOKEN_RECOVERY_KEY, GUEST_IN_USE_KEY, GUEST_IN_USE_BEAT_MS, GUEST_ACTIVE_PROJECT_KEY };
